/**
 * ManuscriptExportPanel — Admin bulk ZIP export of AI manuscripts + metadata.
 *
 * Calls:
 *   GET  /api/admin/manuscript-export/browse?q=...&page=0&per_page=100
 *   POST /api/admin/manuscript-export/bulk-download  { project_ids: [...] }
 *   GET  /api/admin/manuscript-export/{user_id}/download   ← per-user ZIP
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Archive, BookOpen, ChevronDown, ChevronUp, Download,
  Loader2, RefreshCw, Search, User, X, FileDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmt(n) { return n ? Number(n).toLocaleString() : '—'; }

// ─── Export Progress Overlay ─────────────────────────────────────────────────
function ExportProgress({ state }) {
  // state = { active, label, count, elapsed }
  if (!state.active) return null;

  // Fake smooth progress: reaches ~85 % over 40 s, never completes until done
  const pct = Math.min(85, Math.round((state.elapsed / 40) * 85));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/75 backdrop-blur-sm"
      data-testid="ms-export-progress-overlay"
    >
      <div className="w-[380px] rounded-xl border border-border bg-card shadow-2xl p-6 space-y-5">
        {/* Title row */}
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-full bg-primary/10 p-2">
            <Archive className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-[14px] font-semibold leading-snug">{state.label}</p>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              {state.count} manuscript{state.count !== 1 ? 's' : ''} · building DOCX files…
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-1000 ease-linear"
              style={{ width: `${pct}%` }}
              data-testid="ms-export-progress-bar"
            />
          </div>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Compiling &amp; zipping…
            </span>
            <span>{state.elapsed}s elapsed</span>
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
          Large exports may take 30–90 seconds. The ZIP will download automatically when ready.
        </p>
      </div>
    </div>
  );
}

// ─── Per-user download hook ───────────────────────────────────────────────────
// Returns { downloading: Set<userId>, startUserDownload }
function useUserDownload(onProgress, onDone) {
  const [downloading, setDownloading] = useState(new Set());

  const startUserDownload = useCallback(async (userId, userLabel, userProjectCount) => {
    if (downloading.has(userId)) return;
    setDownloading(prev => new Set([...prev, userId]));
    onProgress({ label: `Exporting ${userLabel}'s manuscripts`, count: userProjectCount });
    try {
      const res = await http.get(
        `/admin/manuscript-export/${encodeURIComponent(userId)}/download`,
        { responseType: 'blob' },
      );
      const count = res.headers['x-project-count'] || userProjectCount;
      const name  = res.headers['x-user-display-name'] || userLabel;
      const url   = URL.createObjectURL(res.data);
      const a     = document.createElement('a');
      const ts    = new Date().toISOString().slice(0, 10);
      a.href     = url;
      a.download = `${name.replace(/[^a-z0-9_-]/gi, '_')}_manuscripts_${ts}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`ZIP downloaded — ${count} manuscript${count !== 1 ? 's' : ''} for ${name}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `User export failed for ${userLabel}.`);
    } finally {
      setDownloading(prev => { const n = new Set(prev); n.delete(userId); return n; });
      onDone();
    }
  }, [downloading, onProgress, onDone]);

  return { downloading, startUserDownload };
}

// ─── Row ─────────────────────────────────────────────────────────────────────
function ProjectRow({ project, checked, onToggle, onUserDownload, userDownloading }) {
  const isUserDl = userDownloading.has(project.user_id);

  return (
    <tr
      className={cn(
        'border-b border-border text-[12.5px] hover:bg-muted/30 transition-colors cursor-pointer group',
        checked && 'bg-primary/5',
      )}
      onClick={() => onToggle(project.project_id)}
      data-testid={`ms-export-row-${project.project_id}`}
    >
      {/* Checkbox */}
      <td className="pl-3 pr-1 py-2 w-8">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(project.project_id)}
          onClick={e => e.stopPropagation()}
          className="h-3.5 w-3.5 rounded accent-primary cursor-pointer"
          data-testid={`ms-export-check-${project.project_id}`}
        />
      </td>

      {/* Title / genre */}
      <td className="py-2 pr-3 max-w-[180px]">
        <p className="font-medium leading-tight truncate">{project.title || 'Untitled'}</p>
        {project.genre && (
          <p className="text-[10.5px] text-muted-foreground mt-0.5 truncate">{project.genre}</p>
        )}
      </td>

      {/* Author + per-user download */}
      <td className="py-2 pr-2 text-muted-foreground max-w-[200px]">
        <div className="flex items-center justify-between gap-1">
          <div className="min-w-0">
            <div className="flex items-center gap-1 truncate">
              <User className="h-3 w-3 shrink-0" />
              <span className="truncate">{project.user_name || project.user_email}</span>
            </div>
            <p className="text-[10.5px] mt-0.5 truncate opacity-70">{project.user_email}</p>
          </div>
          {/* Per-user ZIP button — stops row toggle from firing */}
          <button
            onClick={e => {
              e.stopPropagation();
              onUserDownload(
                project.user_id,
                project.user_name || project.user_email,
              );
            }}
            disabled={isUserDl || !project.user_id}
            title={`Download all of ${project.user_name || project.user_email}'s manuscripts`}
            className={cn(
              'shrink-0 rounded-md p-1 transition-colors',
              'opacity-0 group-hover:opacity-100',
              'text-muted-foreground hover:text-primary hover:bg-primary/10',
              'disabled:pointer-events-none disabled:opacity-40',
            )}
            data-testid={`ms-export-user-dl-${project.user_id}`}
          >
            {isUserDl
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <FileDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </td>

      {/* Chapters */}
      <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
        {project.chapter_count}
      </td>

      {/* Words */}
      <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
        {fmt(project.word_count)}
      </td>

      {/* Created */}
      <td className="py-2 pr-3 text-right text-muted-foreground">
        {project.created_at || '—'}
      </td>
    </tr>
  );
}

// ─── Main panel ──────────────────────────────────────────────────────────────
export default function ManuscriptExportPanel() {
  const [items,    setItems]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(0);
  const [q,        setQ]        = useState('');
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [sortBy,   setSortBy]   = useState('created_at');
  const [sortDir,  setSortDir]  = useState('desc');

  // Export progress state
  const [exportProg, setExportProg] = useState({ active: false, label: '', count: 0, elapsed: 0 });
  const timerRef = useRef(null);

  const startProgress = useCallback(({ label, count }) => {
    setExportProg({ active: true, label, count, elapsed: 0 });
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setExportProg(prev => ({ ...prev, elapsed: prev.elapsed + 1 }));
    }, 1000);
  }, []);

  const stopProgress = useCallback(() => {
    clearInterval(timerRef.current);
    setExportProg({ active: false, label: '', count: 0, elapsed: 0 });
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const PER_PAGE  = 100;
  const searchRef = useRef(null);
  const searchTimer = useRef(null);

  const load = useCallback(async (pageOverride, qOverride) => {
    setLoading(true);
    const pg    = pageOverride ?? page;
    const query = qOverride   ?? q;
    try {
      const res = await http.get('/admin/manuscript-export/browse', {
        params: { page: pg, per_page: PER_PAGE, q: query || undefined },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Could not load project list.');
    } finally {
      setLoading(false);
    }
  }, [page, q]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (v) => {
    setQ(v);
    setPage(0);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(0, v), 400);
  };

  // ── Select / deselect ──
  const toggleOne = (id) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const allChecked = items.length > 0 && items.every(it => selected.has(it.project_id));
  const toggleAll  = () => {
    setSelected(prev => {
      const n = new Set(prev);
      if (allChecked) items.forEach(it => n.delete(it.project_id));
      else            items.forEach(it => n.add(it.project_id));
      return n;
    });
  };

  // ── Sort ──
  const handleSort = (col) => {
    sortBy === col ? setSortDir(d => d === 'asc' ? 'desc' : 'asc') : (setSortBy(col), setSortDir('desc'));
  };
  const sorted = [...items].sort((a, b) => {
    const av = a[sortBy] ?? '', bv = b[sortBy] ?? '';
    const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // ── Bulk export ──
  const [bulkExporting, setBulkExporting] = useState(false);
  const handleBulkExport = async (allProjects = false) => {
    const ids = allProjects ? ['__all__'] : Array.from(selected);
    if (!ids.length) { toast.error('Select at least one project.'); return; }
    const count = allProjects ? total : ids.length;
    setBulkExporting(true);
    startProgress({ label: allProjects ? `Exporting all ${total} projects` : `Exporting ${count} selected project${count !== 1 ? 's' : ''}`, count });
    try {
      const res = await http.post(
        '/admin/manuscript-export/bulk-download',
        { project_ids: ids },
        { responseType: 'blob' },
      );
      const actualCount = res.headers['x-project-count'] || count;
      const url = URL.createObjectURL(res.data);
      const a   = document.createElement('a');
      const ts  = new Date().toISOString().slice(0, 10);
      a.href     = url;
      a.download = `manuscripts_${ts}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`ZIP downloaded — ${actualCount} manuscript${actualCount !== 1 ? 's' : ''} included.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed. Please try again.');
    } finally {
      setBulkExporting(false);
      stopProgress();
    }
  };

  // ── Per-user export ──
  // Count projects per user_id on the current page (used for progress label)
  const userProjectCounts = items.reduce((acc, it) => {
    if (it.user_id) acc[it.user_id] = (acc[it.user_id] || 0) + 1;
    return acc;
  }, {});

  const { downloading: userDownloading, startUserDownload } = useUserDownload(
    startProgress,
    stopProgress,
  );

  const handleUserDownload = (userId, userLabel) => {
    startUserDownload(userId, userLabel, userProjectCounts[userId] || 1);
  };

  const exporting = bulkExporting || userDownloading.size > 0;

  // ── UI helpers ──
  const totalPages = Math.ceil(total / PER_PAGE);
  const SortIcon   = ({ col }) => {
    if (sortBy !== col) return <ChevronDown className="h-3 w-3 opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />;
  };
  const thClass = 'px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground text-left select-none cursor-pointer hover:text-foreground';

  return (
    <>
      <ExportProgress state={exportProg} />

      <div className="p-4 space-y-4" data-testid="manuscript-export-panel">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-[15px] font-semibold flex items-center gap-2">
              <Archive className="h-4 w-4 text-primary" /> Bulk Manuscript Export
            </h2>
            <p className="text-[12.5px] text-muted-foreground mt-0.5">
              Export AI manuscripts + full metadata (characters, world bible, plot, codex, continuity)
              as DOCX files packed in a ZIP. Hover any row to download a single user's ZIP directly.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm" variant="outline"
              onClick={() => load()}
              disabled={loading || exporting}
              className="gap-1.5 h-8"
              data-testid="ms-export-refresh"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
              Refresh
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => handleBulkExport(false)}
              disabled={exporting || selected.size === 0}
              className="gap-1.5 h-8"
              data-testid="ms-export-selected-btn"
            >
              {bulkExporting
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Download className="h-3.5 w-3.5" />}
              Export selected ({selected.size})
            </Button>
            <Button
              size="sm"
              onClick={() => handleBulkExport(true)}
              disabled={exporting}
              className="gap-1.5 h-8"
              data-testid="ms-export-all-btn"
            >
              {bulkExporting
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Archive className="h-3.5 w-3.5" />}
              Export all ({total})
            </Button>
          </div>
        </div>

        {/* ── Search + stat bar ── */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <input
              ref={searchRef}
              value={q}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search title, genre, author…"
              className="w-full rounded-md border border-border bg-background pl-8 pr-8 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid="ms-export-search"
            />
            {q && (
              <button
                onClick={() => { setQ(''); load(0, ''); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground shrink-0">
            <BookOpen className="h-3.5 w-3.5" />
            <span>{total.toLocaleString()} project{total !== 1 ? 's' : ''}</span>
            {selected.size > 0 && (
              <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10.5px] font-medium bg-primary/10 text-primary">
                {selected.size} selected
              </span>
            )}
          </div>
        </div>

        {/* ── Table ── */}
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/40 border-b border-border">
                <tr>
                  <th className="pl-3 pr-1 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                      className="h-3.5 w-3.5 rounded accent-primary cursor-pointer"
                      data-testid="ms-export-check-all"
                    />
                  </th>
                  <th className={thClass} onClick={() => handleSort('title')}>
                    <span className="flex items-center gap-1">Title <SortIcon col="title" /></span>
                  </th>
                  <th className={thClass} onClick={() => handleSort('user_email')}>
                    <span className="flex items-center gap-1">Author <SortIcon col="user_email" /></span>
                  </th>
                  <th className={cn(thClass, 'text-right')} onClick={() => handleSort('chapter_count')}>
                    <span className="flex items-center justify-end gap-1">Ch. <SortIcon col="chapter_count" /></span>
                  </th>
                  <th className={cn(thClass, 'text-right')} onClick={() => handleSort('word_count')}>
                    <span className="flex items-center justify-end gap-1">Words <SortIcon col="word_count" /></span>
                  </th>
                  <th className={cn(thClass, 'text-right')} onClick={() => handleSort('created_at')}>
                    <span className="flex items-center justify-end gap-1">Created <SortIcon col="created_at" /></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-muted-foreground text-[13px]">
                      <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                      Loading projects…
                    </td>
                  </tr>
                ) : sorted.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-muted-foreground text-[13px]">
                      {q ? 'No projects match your search.' : 'No projects found in the database.'}
                    </td>
                  </tr>
                ) : (
                  sorted.map(project => (
                    <ProjectRow
                      key={project.project_id}
                      project={project}
                      checked={selected.has(project.project_id)}
                      onToggle={toggleOne}
                      onUserDownload={handleUserDownload}
                      userDownloading={userDownloading}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between text-[12.5px]">
            <span className="text-muted-foreground">Page {page + 1} of {totalPages}</span>
            <div className="flex gap-2">
              <Button
                size="sm" variant="outline" className="h-7 text-[12px]"
                disabled={page === 0 || loading}
                onClick={() => { const p = page - 1; setPage(p); load(p); }}
                data-testid="ms-export-prev"
              >Previous</Button>
              <Button
                size="sm" variant="outline" className="h-7 text-[12px]"
                disabled={page >= totalPages - 1 || loading}
                onClick={() => { const p = page + 1; setPage(p); load(p); }}
                data-testid="ms-export-next"
              >Next</Button>
            </div>
          </div>
        )}

        {/* ── Tip footer ── */}
        <p className="text-[11.5px] text-muted-foreground border-t border-border pt-3">
          Hover any row to reveal the <FileDown className="inline h-3 w-3 mx-0.5" /> user ZIP button — downloads all
          manuscripts for that author in one click. Each DOCX includes the full manuscript text plus a
          metadata appendix: character bibles, world bible, codex/lore, plot beats, notebook notes,
          continuity findings, edit-walker passes, voice audit, readability scores, and editorial records.
        </p>
      </div>
    </>
  );
}
